import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { FacialVerificationModal } from '../../components/banking/FacialVerificationModal';
import {
  Wallet,
  ArrowUpRight,
  Clock,
  Building,
  QrCode,
  Calendar,
  X,
  Check,
  AlertTriangle,
} from 'lucide-react';

type WithdrawalTab = 'withdraw' | 'scheduled' | 'history';
type WithdrawalMethod = 'BANK' | 'PIX';

export function SupplierWithdrawals() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<WithdrawalTab>('withdraw');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Facial verification state
  const [showFacialVerification, setShowFacialVerification] = useState(false);
  const [verificationSessionId, setVerificationSessionId] = useState<string | undefined>();
  const [verificationReason, setVerificationReason] = useState<string | undefined>();
  const [pendingWithdrawalData, setPendingWithdrawalData] = useState<any>(null);
  
  // Form state
  const [method, setMethod] = useState<WithdrawalMethod>('PIX');
  const [amount, setAmount] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [agency, setAgency] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountType, setAccountType] = useState('CHECKING');
  const [scheduleDate, setScheduleDate] = useState('');

  // Fetch balance
  const { data: balanceData } = useQuery({
    queryKey: ['supplier', 'balance'],
    queryFn: async () => {
      const response = await api.get('/banking/supplier/balance');
      return response.data;
    },
  });


  // Fetch scheduled withdrawals
  const { data: scheduledData, isLoading: scheduledLoading } = useQuery({
    queryKey: ['supplier', 'withdrawals', 'scheduled'],
    queryFn: async () => {
      const response = await api.get('/banking/supplier/withdrawals/scheduled');
      return response.data;
    },
    enabled: activeTab === 'scheduled',
  });

  // Fetch withdrawal history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['supplier', 'withdrawals', 'history'],
    queryFn: async () => {
      const response = await api.get('/banking/supplier/withdrawals/history');
      return response.data;
    },
    enabled: activeTab === 'history',
  });

  // Withdraw mutation
  const withdrawMutation = useMutation({
    mutationFn: async (data: any) => {
      const endpoint = method === 'PIX' 
        ? '/banking/supplier/withdrawals/pix'
        : '/banking/supplier/withdrawals/bank';
      const response = await api.post(endpoint, data);
      return response.data;
    },
    onSuccess: (data) => {
      // Check if facial verification is required
      if (data.requiresVerification) {
        setVerificationSessionId(data.sessionId);
        setVerificationReason(data.reason);
        const amountCents = parseAmount(amount);
        setPendingWithdrawalData({
          amount: amountCents,
          pixKey: method === 'PIX' ? pixKey : undefined,
          bankCode: method === 'BANK' ? bankCode : undefined,
          agency: method === 'BANK' ? agency : undefined,
          accountNumber: method === 'BANK' ? accountNumber : undefined,
          accountType: method === 'BANK' ? accountType : undefined,
        });
        setShowConfirmModal(false);
        setShowFacialVerification(true);
        return;
      }
      
      queryClient.invalidateQueries({ queryKey: ['supplier', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['supplier', 'withdrawals'] });
      setShowConfirmModal(false);
      resetForm();
    },
  });

  // Schedule withdrawal mutation
  const scheduleWithdrawalMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/banking/supplier/withdrawals/schedule', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', 'withdrawals', 'scheduled'] });
      resetForm();
    },
  });

  // Cancel scheduled withdrawal mutation
  const cancelScheduledMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/banking/supplier/withdrawals/scheduled/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', 'withdrawals', 'scheduled'] });
    },
  });

  const resetForm = () => {
    setAmount('');
    setPixKey('');
    setBankCode('');
    setAgency('');
    setAccountNumber('');
    setScheduleDate('');
    setPendingWithdrawalData(null);
    setVerificationSessionId(undefined);
    setVerificationReason(undefined);
  };

  // Handle facial verification completed
  const handleFacialVerificationComplete = async (sessionId: string) => {
    setShowFacialVerification(false);
    
    if (pendingWithdrawalData) {
      // Re-send the withdrawal with the verification session ID
      withdrawMutation.mutate({
        ...pendingWithdrawalData,
        verificationSessionId: sessionId,
      });
    }
  };

  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseInt(value, 10) || 0 : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(num / 100);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  const parseAmount = (value: string): number => {
    const cleaned = value.replace(/\D/g, '');
    return parseInt(cleaned, 10) || 0;
  };

  const handleAmountChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    const cents = parseInt(numericValue, 10) || 0;
    const formatted = (cents / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setAmount(formatted);
  };

  const handleWithdraw = () => {
    const amountCents = parseAmount(amount);
    
    if (scheduleDate) {
      scheduleWithdrawalMutation.mutate({
        method,
        amount: amountCents,
        pixKey: method === 'PIX' ? pixKey : undefined,
        bankAccount: method === 'BANK' ? { bankCode, agency, accountNumber, accountType } : undefined,
        scheduledDate: scheduleDate,
      });
    } else {
      const data: any = { amount: amountCents };
      if (method === 'PIX') {
        data.pixKey = pixKey;
      } else {
        data.bankCode = bankCode;
        data.agency = agency;
        data.accountNumber = accountNumber;
        data.accountType = accountType;
      }
      withdrawMutation.mutate(data);
    }
  };

  const tabs = [
    { id: 'withdraw' as WithdrawalTab, label: 'Sacar', icon: <ArrowUpRight size={18} /> },
    { id: 'scheduled' as WithdrawalTab, label: 'Agendados', icon: <Clock size={18} /> },
    { id: 'history' as WithdrawalTab, label: 'Histórico', icon: <Wallet size={18} /> },
  ];

  const bankOptions = [
    { value: '001', label: '001 - Banco do Brasil' },
    { value: '033', label: '033 - Santander' },
    { value: '104', label: '104 - Caixa' },
    { value: '237', label: '237 - Bradesco' },
    { value: '341', label: '341 - Itaú' },
    { value: '260', label: '260 - Nubank' },
    { value: '077', label: '077 - Inter' },
    { value: '336', label: '336 - C6 Bank' },
  ];

  const accountTypeOptions = [
    { value: 'CHECKING', label: 'Conta Corrente' },
    { value: 'SAVINGS', label: 'Poupança' },
  ];


  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Saques</h1>
        <p className="text-surface-400">Transfira seu saldo para sua conta bancária</p>
      </div>

      {/* Balance Card */}
      <Card className="bg-gradient-to-br from-accent-600 to-accent-800 border-0">
        <div className="p-6">
          <p className="text-accent-200 mb-2">Saldo disponível para saque</p>
          <p className="text-3xl font-bold text-white">
            {formatCurrency(balanceData?.availableBalance || 0)}
          </p>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
              transition-colors
              ${activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
              }
            `}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Withdraw Tab */}
      {activeTab === 'withdraw' && (
        <Card>
          <div className="p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white">Realizar Saque</h2>

            {/* Method Selection */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Método de saque
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setMethod('PIX')}
                  className={`
                    flex items-center justify-center gap-2 p-4 rounded-lg transition-colors
                    ${method === 'PIX'
                      ? 'bg-primary-500 text-white'
                      : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                    }
                  `}
                >
                  <QrCode size={24} />
                  <span>Pix</span>
                </button>
                <button
                  onClick={() => setMethod('BANK')}
                  className={`
                    flex items-center justify-center gap-2 p-4 rounded-lg transition-colors
                    ${method === 'BANK'
                      ? 'bg-primary-500 text-white'
                      : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                    }
                  `}
                >
                  <Building size={24} />
                  <span>TED/DOC</span>
                </button>
              </div>
            </div>

            {/* Amount */}
            <Input
              label="Valor do saque"
              placeholder="0,00"
              value={amount}
              onChange={(e) => handleAmountChange(e.target.value)}
              leftIcon={<span className="text-surface-400">R$</span>}
            />

            {/* PIX Fields */}
            {method === 'PIX' && (
              <Input
                label="Chave Pix de destino"
                placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória"
                value={pixKey}
                onChange={(e) => setPixKey(e.target.value)}
              />
            )}

            {/* Bank Fields */}
            {method === 'BANK' && (
              <div className="space-y-4">
                <Select
                  label="Banco"
                  value={bankCode}
                  onChange={(e) => setBankCode(e.target.value)}
                  options={[{ value: '', label: 'Selecione o banco' }, ...bankOptions]}
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Agência"
                    placeholder="0000"
                    value={agency}
                    onChange={(e) => setAgency(e.target.value)}
                  />
                  <Input
                    label="Conta"
                    placeholder="00000-0"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                  />
                </div>
                <Select
                  label="Tipo de conta"
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                  options={accountTypeOptions}
                />
              </div>
            )}

            {/* Schedule Date */}
            <Input
              label="Agendar para (opcional)"
              type="datetime-local"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              leftIcon={<Calendar size={18} className="text-surface-400" />}
            />

            <Button
              variant="primary"
              className="w-full"
              onClick={() => setShowConfirmModal(true)}
              disabled={
                !amount ||
                (method === 'PIX' && !pixKey) ||
                (method === 'BANK' && (!bankCode || !agency || !accountNumber))
              }
            >
              {scheduleDate ? 'Agendar Saque' : 'Sacar Agora'}
            </Button>
          </div>
        </Card>
      )}

      {/* Scheduled Tab */}
      {activeTab === 'scheduled' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Saques Agendados</h2>

            {scheduledLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-4 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                    <div className="h-3 w-48 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : scheduledData?.items?.length > 0 ? (
              <div className="space-y-3">
                {scheduledData.items.map((withdrawal: any) => (
                  <div
                    key={withdrawal.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-surface-800/50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <Clock size={20} className="text-primary-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">
                          {withdrawal.method === 'PIX' ? 'Pix' : 'TED/DOC'}
                        </p>
                        <p className="text-xs text-surface-400">
                          Agendado para {formatDate(withdrawal.scheduledDate)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-semibold text-white">
                        {formatCurrency(withdrawal.amount)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => cancelScheduledMutation.mutate(withdrawal.id)}
                      >
                        <X size={16} className="text-red-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum saque agendado</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Histórico de Saques</h2>

            {historyLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-24 bg-surface-700 rounded" />
                    </div>
                    <div className="h-5 w-20 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : historyData?.items?.length > 0 ? (
              <div className="space-y-2">
                {historyData.items.map((withdrawal: any) => (
                  <div
                    key={withdrawal.id}
                    className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50"
                  >
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${withdrawal.status === 'COMPLETED'
                        ? 'bg-green-500/20 text-green-400'
                        : withdrawal.status === 'FAILED'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                      }
                    `}>
                      {withdrawal.status === 'COMPLETED'
                        ? <Check size={20} />
                        : withdrawal.status === 'FAILED'
                        ? <AlertTriangle size={20} />
                        : <Clock size={20} />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">
                        {withdrawal.method === 'PIX' ? 'Saque via Pix' : 'Saque via TED'}
                      </p>
                      <p className="text-xs text-surface-400">
                        {formatDate(withdrawal.createdAt)}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-white">
                      {formatCurrency(withdrawal.amount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Wallet size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum saque realizado</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Confirm Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmar Saque"
      >
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-surface-800">
            <div className="flex justify-between mb-2">
              <span className="text-surface-400">Método</span>
              <span className="text-white">{method === 'PIX' ? 'Pix' : 'TED/DOC'}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-surface-400">Valor</span>
              <span className="text-white font-semibold">R$ {amount}</span>
            </div>
            {method === 'PIX' && (
              <div className="flex justify-between">
                <span className="text-surface-400">Chave Pix</span>
                <span className="text-white truncate max-w-[200px]">{pixKey}</span>
              </div>
            )}
            {method === 'BANK' && (
              <>
                <div className="flex justify-between">
                  <span className="text-surface-400">Banco</span>
                  <span className="text-white">{bankCode}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Agência/Conta</span>
                  <span className="text-white">{agency} / {accountNumber}</span>
                </div>
              </>
            )}
            {scheduleDate && (
              <div className="flex justify-between mt-2">
                <span className="text-surface-400">Agendado para</span>
                <span className="text-white">{formatDate(scheduleDate)}</span>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleWithdraw}
              disabled={withdrawMutation.isPending || scheduleWithdrawalMutation.isPending}
            >
              {withdrawMutation.isPending || scheduleWithdrawalMutation.isPending
                ? 'Processando...'
                : 'Confirmar'
              }
            </Button>
          </div>
        </div>
      </Modal>

      {/* Facial Verification Modal */}
      <FacialVerificationModal
        isOpen={showFacialVerification}
        onClose={() => {
          setShowFacialVerification(false);
          setPendingWithdrawalData(null);
        }}
        onVerified={handleFacialVerificationComplete}
        operationType="WITHDRAWAL"
        operationValue={pendingWithdrawalData?.amount}
        sessionId={verificationSessionId}
        reason={verificationReason}
      />
    </div>
  );
}

export default SupplierWithdrawals;
